import { ColumnProps, useTheme } from "@monolith-forensics/monolith-ui";
import { useMemo } from "react";
import moment from "moment";
import {
  CUSTOM_FIELD_CATEGORIES,
  CustomFieldsQuery,
} from "@/Features/CustomFields";
import CaseNumberCell from "@/components/Evidence/CaseNumberCell";
import EncryptionKeyComponent from "@/components/CaseStorage/EncryptionKeyComponent";
import CaseStorageCell from "@/components/CaseStorage/StorageNumberCell";
import StorageCurrentLocationCell from "@/components/CaseStorage/StorageCurrentLocationCell";
import CustomFieldCell from "@/components/Evidence/CustomFieldCell";
import { StorageItem } from "./types";

/**
 * Renders a percentage bar using theme-aware colors.
 * @param pct - value between 0 and 1
 * @param formattedPct - display string e.g. "45.00%"
 * @param invert - when true, lower values are bad (used for "available" percentage)
 */
const PercentageBar = ({
  pct,
  formattedPct,
  invert = false,
}: {
  pct: number;
  formattedPct: string;
  invert?: boolean;
}) => {
  const theme = useTheme();

  const getColor = () => {
    const effectivePct = invert ? 1 - pct : pct;
    if (effectivePct > 0.8) return theme.palette.error.main;
    if (effectivePct > 0.6) return theme.alert.warning.color;
    return theme.alert.success.color;
  };

  return (
    <div style={{ position: "relative" }}>
      <div>{formattedPct}</div>
      <div
        style={{
          width: formattedPct,
          height: 4,
          backgroundColor: getColor(),
        }}
      />
    </div>
  );
};

/**
 * Shared guard/clamp/format for the percent columns: computes
 * numeratorBytes / capacityBytes, clamps to 0..1, and renders a
 * PercentageBar. `invert` marks columns where lower values are bad.
 */
const renderUsagePct = (
  numeratorBytes: number | null | undefined,
  capacityBytes: number | null | undefined,
  invert = false
) => {
  if (!numeratorBytes || !capacityBytes) return "0%";
  const pct = numeratorBytes / capacityBytes;
  if (pct > 1) return "100%";
  if (pct < 0) return "0%";

  const formattedPct = Number(pct * 100).toFixed(2) + "%";

  return (
    <PercentageBar pct={pct} formattedPct={formattedPct} invert={invert} />
  );
};

export const StorageColumnDefinitions: ColumnProps[] = [
  {
    dataField: "storage_number",
    caption: "Storage Number",
    render: ({ rowData }: { rowData: StorageItem }) => {
      return <CaseStorageCell rowData={rowData} />;
    },
  },
  {
    dataField: "uuid",
    caption: "UUID",
    visible: false,
  },
  {
    dataField: "case_number",
    caption: "Case Number",
    visible: false,
    render: ({ rowData }: { rowData: StorageItem }) => {
      return <CaseNumberCell rowData={rowData} />;
    },
  },
  {
    dataField: "case_ref",
    caption: "Case Name",
    visible: false,
  },
  {
    dataField: "created_on",
    caption: "Created On",
    render: ({ rowData }: { rowData: StorageItem }) => {
      return rowData.created_on
        ? moment(rowData.created_on).format("YYYY-MM-DD")
        : "";
    },
  },
  {
    dataField: "last_audit_date",
    caption: "Last Audit Date",
    visible: false,
    render: ({ rowData }: { rowData: StorageItem }) => {
      return rowData.last_audit_date
        ? moment(rowData.last_audit_date).format("YYYY-MM-DD")
        : "";
    },
  },
  {
    dataField: "date_assigned",
    caption: "Date Assigned",
    visible: false,
    render: ({ rowData }: { rowData: StorageItem }) => {
      return rowData.date_assigned
        ? moment(rowData.date_assigned).format("YYYY-MM-DD")
        : "";
    },
  },
  {
    dataField: "encryption_key",
    caption: "Encryption Key",
    render: ({ rowData }: { rowData: StorageItem }) => (
      <EncryptionKeyComponent rowData={rowData} />
    ),
  },
  {
    dataField: "general_storage",
    caption: "General Storage",
    visible: false,
  },
  {
    dataField: "type",
    caption: "Type",
  },
  {
    dataField: "make",
    caption: "Provider",
  },
  {
    dataField: "model_name",
    caption: "Model",
  },
  {
    dataField: "model_number",
    caption: "Model Number",
    visible: false,
  },
  {
    dataField: "serial_number",
    caption: "Serial Number",
    visible: false,
  },
  {
    dataField: "capacity_bytes",
    caption: "Size",
    visible: false,
    render: ({ rowData }: { rowData: StorageItem }) =>
      rowData.capacity
        ? `${rowData.capacity} ${rowData.capacity_unit || ""}`
        : "",
  },
  {
    dataField: "capacity_gb",
    caption: "Size (GB)",
    visible: false,
    render: ({ rowData }: { rowData: StorageItem }) => {
      if (!rowData.capacity_gb) return "";
      return rowData.capacity_gb + " GB";
    },
  },
  {
    dataField: "stored_bytes",
    caption: "Stored Data (GB)",
    visible: false,
    render: ({ rowData }: { rowData: StorageItem }) => {
      // convert to GB
      if (!rowData.stored_bytes) return "0";
      return Number(
        (rowData.stored_bytes / (1024 * 1024 * 1024)).toFixed(2)
      ).toLocaleString();
    },
  },
  {
    dataField: "free_bytes",
    caption: "Free Space (GB)",
    visible: false,
    render: ({ rowData }: { rowData: StorageItem }) => {
      // convert to GB
      if (!rowData.free_bytes) return "0";
      return Number(
        (rowData.free_bytes / (1024 * 1024 * 1024)).toFixed(2)
      ).toLocaleString();
    },
  },
  {
    dataField: "used_percentage",
    caption: "Percent Used",
    visible: false,
    render: ({ rowData }: { rowData: StorageItem }) =>
      renderUsagePct(rowData.stored_bytes, rowData.capacity_bytes),
  },
  {
    dataField: "available_percentage",
    caption: "Percent Available",
    visible: false,
    render: ({ rowData }: { rowData: StorageItem }) =>
      renderUsagePct(rowData.free_bytes, rowData.capacity_bytes, true),
  },
  {
    dataField: "location_name",
    caption: "Current Location",
    visible: false,
    render: ({ rowData }: { rowData: StorageItem }) => {
      return <StorageCurrentLocationCell rowData={rowData} />;
    },
  },
  {
    dataField: "location_path",
    caption: "Location Path",
    visible: false,
  },
  {
    dataField: "notes",
    caption: "Description",
    visible: false,
  },
];

/**
 * Returns the storage column definitions with dynamic custom-field columns
 * merged in. Mirrors `useEvidenceColumnDefinitions` — custom fields for the
 * STORAGE category are appended as hidden columns rendered via `CustomFieldCell`.
 */
export const useStorageColumnDefinitions = () => {
  const {
    data: customFields,
    isLoading,
    isSuccess,
    isError,
    refetch,
  } = CustomFieldsQuery.useGetCustomFields({
    category: CUSTOM_FIELD_CATEGORIES.STORAGE,
  });

  // Memoized so the Table's columnProps keep a stable identity across renders
  // (column reorder/resize/state churn otherwise) — mirrors
  // useStorageFilterDefinitions.
  const columnDefinitions: ColumnProps[] = useMemo(() => {
    if (!customFields) return StorageColumnDefinitions;
    return [
      ...StorageColumnDefinitions,
      ...customFields.data.map((field) => ({
        dataField: `custom_field_${field.field_id}`,
        caption: field.field_name,
        visible: false,
        render: ({ rowData }: { rowData: StorageItem }) => {
          // does this custom field have a value?
          if (
            !rowData.custom_attributes?.find(
              (attr) => attr.field_id === field.field_id
            )
          ) {
            return null;
          }
          return <CustomFieldCell rowData={rowData} field={field} />;
        },
      })),
    ];
  }, [customFields]);

  return {
    columnDefinitions,
    customFields,
    isLoading,
    isSuccess,
    isError,
    refetch,
  };
};
